<!--
  EventDeleteDrawer.vue
  이벤트 삭제 드로워 컴포넌트
  
  - 소프트 삭제와 하드 삭제를 탭으로 구분
  - 하드 삭제 시 위험성 경고 및 확인 문자열 입력 요구
  - 클라우드 서비스의 리소스 삭제 UX 참고
-->
<template>
  <a-drawer
    :visible="visible"
    :width="600"
    title="이벤트 삭제"
    placement="right"
    @close="handleClose"
    :closable="true"
    :destroy-on-close="true"
  >
    <div class="delete-drawer-content">
      <!-- 이벤트 정보 표시 -->
      <div class="event-info-card">
        <div class="info-label">삭제할 이벤트</div>
        <div class="info-value">{{ eventTitle }}</div>
        <div class="info-id">ID: {{ eventId }}</div>
      </div>

      <!-- 탭 메뉴 -->
      <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
        <a-tab-pane key="soft" tab="일반 삭제">
          <div class="delete-section">
            <div class="delete-type-header">
              <i class="fa fa-trash" style="color: #1890ff; margin-right: 8px"></i>
              <span class="delete-type-title">소프트 삭제 (Soft Delete)</span>
            </div>
            
            <div class="delete-description">
              <p>이벤트를 논리적으로 삭제합니다. 데이터는 데이터베이스에 유지되며 필요시 복구가 가능합니다.</p>
            </div>

            <div class="delete-features">
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>데이터베이스에 데이터 유지</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>필요시 복구 가능</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-check-circle" style="color: #52c41a"></i>
                <span>참여자 데이터 보존</span>
              </div>
              <div class="feature-item">
                <i class="fa fa-info-circle" style="color: #1890ff"></i>
                <span>이벤트 목록에서 숨김 처리</span>
              </div>
            </div>

            <div class="warning-box info">
              <i class="fa fa-info-circle"></i>
              <div class="warning-content">
                <div class="warning-title">안내</div>
                <div class="warning-text">이벤트가 목록에서 숨겨지지만 데이터는 보존됩니다.</div>
              </div>
            </div>

            <div class="action-buttons">
              <a-button @click="handleClose">취소</a-button>
              <a-button 
                type="primary" 
                @click="handleSoftDelete"
                :loading="softDeleting"
                class="delete-button soft-delete-btn"
              >
                <i class="fa fa-trash" style="margin-right: 4px"></i>
                소프트 삭제
              </a-button>
            </div>
          </div>
        </a-tab-pane>

        <a-tab-pane key="hard" tab="영구 삭제">
          <div class="delete-section">
            <div class="delete-type-header danger">
              <i class="fa fa-exclamation-triangle" style="color: #ff4d4f; margin-right: 8px"></i>
              <span class="delete-type-title">하드 삭제 (Hard Delete)</span>
            </div>
            
            <div class="delete-description">
              <p style="color: #ff4d4f; font-weight: bold">
                이 작업은 되돌릴 수 없습니다! 이벤트와 관련된 모든 데이터가 영구적으로 삭제됩니다.
              </p>
            </div>

            <div class="delete-features">
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>이벤트 데이터 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>모든 참여자 데이터 영구 삭제</span>
              </div>
              <div class="feature-item danger">
                <i class="fa fa-times-circle" style="color: #ff4d4f"></i>
                <span>복구 불가능</span>
              </div>
              <div class="feature-item danger" v-if="attendeeCount > 0">
                <i class="fa fa-users" style="color: #ff4d4f"></i>
                <span>{{ attendeeCount.toLocaleString() }}명의 참여자 데이터가 삭제됩니다</span>
              </div>
            </div>

            <div class="warning-box danger">
              <i class="fa fa-exclamation-triangle"></i>
              <div class="warning-content">
                <div class="warning-title">경고</div>
                <div class="warning-text">
                  이 작업은 되돌릴 수 없습니다. 이벤트와 관련된 모든 데이터가 데이터베이스에서 영구적으로 삭제됩니다.
                </div>
              </div>
            </div>

            <div class="confirmation-section">
              <div class="confirmation-label">
                영구 삭제를 확인하려면 이벤트 명을 정확히 입력하세요:
              </div>
              <div class="confirmation-target">{{ eventTitle }}</div>
              <a-input 
                v-model:value="confirmationText"
                placeholder="이벤트 명을 입력하세요"
                class="confirmation-input"
                @input="handleConfirmationInput"
              />
              <div class="confirmation-status" v-if="confirmationText">
                <span v-if="isConfirmationValid" class="valid">
                  <i class="fa fa-check-circle"></i> 일치합니다
                </span>
                <span v-else class="invalid">
                  <i class="fa fa-times-circle"></i> 일치하지 않습니다
                </span>
              </div>
            </div>

            <div class="action-buttons">
              <a-button @click="handleClose">취소</a-button>
              <a-button 
                danger
                type="primary"
                @click="handleHardDelete"
                :loading="hardDeleting"
                :disabled="!isConfirmationValid"
                class="delete-button hard-delete-btn"
              >
                <i class="fa fa-trash" style="margin-right: 4px"></i>
                영구 삭제
              </a-button>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>
  </a-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';

export default {
  name: 'EventDeleteDrawer',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    eventId: {
      type: String,
      required: true
    },
    eventTitle: {
      type: String,
      required: true
    },
    attendeeCount: {
      type: Number,
      default: 0
    },
    apiInstance: {
      type: Object,
      required: true
    },
    userRole: {
      type: String,
      default: 'MANUFACTURER'
    }
  },
  emits: ['close', 'delete-success'],
  setup(props, { emit }) {
    const activeTab = ref('soft');
    const confirmationText = ref('');
    const softDeleting = ref(false);
    const hardDeleting = ref(false);

    // 확인 문자열 유효성 검사
    const isConfirmationValid = computed(() => {
      return confirmationText.value === props.eventTitle;
    });

    // 드로워 닫기
    const handleClose = () => {
      confirmationText.value = '';
      activeTab.value = 'soft';
      emit('close');
    };

    // 탭 변경 시 확인 문자열 초기화
    const handleTabChange = () => {
      confirmationText.value = '';
    };

    // 확인 문자열 입력 처리
    const handleConfirmationInput = () => {
      // 입력 처리 로직 (필요시 추가)
    };

    // 소프트 삭제 처리
    const handleSoftDelete = async () => {
      const confirmed = await twoBtnDialog(
        '이벤트 삭제 확인',
        `"${props.eventTitle}" 이벤트를 삭제하시겠습니까? 이 작업은 되돌릴 수 있습니다.`,
        '삭제'
      );
      
      if (confirmed) {
        softDeleting.value = true;
        try {
          await new Promise((resolve, reject) => {
            props.apiInstance.deleteEvent(
              props.eventId,
              (response) => {
                message.success('이벤트가 성공적으로 삭제되었습니다.');
                emit('delete-success', 'soft');
                handleClose();
                resolve(response);
              },
              (error) => {
                console.error('소프트 삭제 실패:', error);
                message.error('이벤트 삭제 중 오류가 발생했습니다.');
                reject(error);
              }
            );
          });
        } catch (error) {
          console.error('소프트 삭제 오류:', error);
        } finally {
          softDeleting.value = false;
        }
      }
    };

    // 하드 삭제 처리
    const handleHardDelete = async () => {
      if (!isConfirmationValid.value) {
        message.warning('이벤트 명을 정확히 입력해주세요.');
        return;
      }

      const confirmMessage = `정말로 이 이벤트를 영구 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며 다음 데이터가 영구 삭제됩니다`;
      
      const confirmed = await twoBtnDialog(
        '영구 삭제 최종 확인',
        confirmMessage,
        '영구 삭제'
      );
      
      if (confirmed) {
        hardDeleting.value = true;
        try {
          await new Promise((resolve, reject) => {
            props.apiInstance.hardDeleteEvent(
              props.eventId,
              (response) => {
                const data = response.data?.data || response.data;
                const deletedCount = data.deletedAttendeesCount || 0;
                message.success(`이벤트와 ${deletedCount.toLocaleString()}명의 참여자 데이터가 영구 삭제되었습니다.`);
                emit('delete-success', 'hard');
                handleClose();
                resolve(response);
              },
              (error) => {
                console.error('하드 삭제 실패:', error);
                if (error.response?.status === 403) {
                  message.error('하드 삭제 권한이 없습니다. CERTILIFE 권한이 필요합니다.');
                } else {
                  message.error('이벤트 영구 삭제 중 오류가 발생했습니다.');
                }
                reject(error);
              }
            );
          });
        } catch (error) {
          console.error('하드 삭제 오류:', error);
        } finally {
          hardDeleting.value = false;
        }
      }
    };

    // props 변경 감지하여 초기화
    watch(() => props.visible, (newVal) => {
      if (newVal) {
        confirmationText.value = '';
        activeTab.value = 'soft';
      }
    });

    return {
      activeTab,
      confirmationText,
      softDeleting,
      hardDeleting,
      isConfirmationValid,
      handleClose,
      handleTabChange,
      handleConfirmationInput,
      handleSoftDelete,
      handleHardDelete
    };
  }
};
</script>

<style scoped lang="scss">
.delete-drawer-content {
  padding: 0;
}

.event-info-card {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;

  .info-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 8px;
  }

  .info-value {
    font-size: 16px;
    font-weight: bold;
    color: #000;
    margin-bottom: 4px;
  }

  .info-id {
    font-size: 12px;
    color: #999;
    font-family: monospace;
  }
}

.delete-section {
  padding: 16px 0;
}

.delete-type-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e8e8e8;

  &.danger {
    border-bottom-color: #ffccc7;
  }

  .delete-type-title {
    font-size: 18px;
    font-weight: bold;
    color: #000;
  }
}

.delete-description {
  margin-bottom: 20px;

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.delete-features {
  margin-bottom: 24px;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 14px;

    i {
      font-size: 16px;
    }

    &.danger {
      color: #ff4d4f;
    }
  }
}

.warning-box {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 24px;

  &.info {
    background: #e6f7ff;
    border: 1px solid #91d5ff;
    color: #1890ff;

    i {
      color: #1890ff;
      font-size: 16px;
      margin-top: 2px;
    }
  }

  &.danger {
    background: #fff2f0;
    border: 1px solid #ffccc7;
    color: #ff4d4f;

    i {
      color: #ff4d4f;
      font-size: 16px;
      margin-top: 2px;
    }
  }

  .warning-content {
    flex: 1;

    .warning-title {
      font-weight: bold;
      margin-bottom: 4px;
    }

    .warning-text {
      font-size: 13px;
      line-height: 1.5;
    }
  }
}

.permission-notice {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #ff4d4f;
  margin-bottom: 24px;
  font-size: 14px;
}

.confirmation-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #d9d9d9;

  .confirmation-label {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }

  .confirmation-target {
    font-size: 14px;
    font-weight: bold;
    color: #000;
    padding: 8px 12px;
    background: white;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    margin-bottom: 12px;
    font-family: monospace;
  }

  .confirmation-input {
    width: 100%;
    margin-bottom: 8px;
  }

  .confirmation-status {
    font-size: 13px;

    .valid {
      color: #52c41a;

      i {
        margin-right: 4px;
      }
    }

    .invalid {
      color: #ff4d4f;

      i {
        margin-right: 4px;
      }
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.delete-button {
  &.soft-delete-btn {
    background-color: #1890ff;
    border-color: #1890ff;

    &:hover:not(:disabled) {
      background-color: #40a9ff;
      border-color: #40a9ff;
    }
  }

  &.hard-delete-btn {
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

:deep(.ant-tabs-tab) {
  font-size: 14px;
  padding: 8px 0;
}

:deep(.ant-tabs-tab-active) {
  font-weight: bold;
}

:deep(.ant-drawer-header) {
  border-bottom: 1px solid #f0f0f0;
}

:deep(.ant-drawer-body) {
  padding: 24px;
}
</style>